import { onBeforeUnmount, watch } from 'vue'
import {
  CONTACT_EMAIL,
  CONTACT_PHONE_TEL,
  SITE_NAME,
  SITE_URL,
} from '../config/contact'

const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.svg`

function upsertMeta(attr, key, content) {
  if (content === undefined || content === null || content === '') return
  let el = document.head.querySelector(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', String(content))
}

function upsertLink(rel, href) {
  if (!href) return
  let el = document.head.querySelector(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id)
  if (!el) {
    el = document.createElement('script')
    el.type = 'application/ld+json'
    el.id = id
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(data)
}

/** Truncate long titles at a word boundary (max 60). Does not invent suffixes. */
export function fitTitle(text, maxLen = 60) {
  const t = String(text || '')
    .replace(/\s+/g, ' ')
    .trim()
  if (t.length <= maxLen) return t
  let cut = t.slice(0, maxLen)
  const sp = cut.lastIndexOf(' ')
  if (sp > 40) cut = cut.slice(0, sp)
  return cut.replace(/[|,\s-]+$/, '')
}

/**
 * Clamp description to max 160. Expands short copy with factual Homvisit Kerala CTAs
 * so every page can meet 140–160 without duplicate fluff-only strings.
 */
export function fitDescription(text, maxLen = 160) {
  let d = String(text || '')
    .replace(/\s+/g, ' ')
    .trim()
  if (d.length < 140) {
    const pads = [
      ' Homvisit serves labs, hospitals, and HR teams across Kerala.',
      ' Call 75 9409 9409 to plan staffing or request a camp quote.',
    ]
    let i = 0
    while (d.length < 140 && i < 4) {
      d = `${d}${pads[i % pads.length]}`.replace(/\s+/g, ' ').trim()
      i += 1
    }
  }
  if (d.length <= maxLen) return d
  let cut = d.slice(0, maxLen - 1)
  const sp = cut.lastIndexOf(' ')
  if (sp > Math.floor(maxLen * 0.6)) cut = cut.slice(0, sp)
  return `${cut.replace(/[.,;:\s-]+$/, '')}…`
}

/**
 * @param {import('vue').Ref|import('vue').ComputedRef|Object} seoSource
 */
export function useSeo(seoSource) {
  const apply = (seo) => {
    if (!seo) return
    const title = fitTitle(seo.title || SITE_NAME)
    const description = fitDescription(seo.description || '')
    const path = seo.path || '/'
    const canonical = `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`
    const keywords = Array.isArray(seo.keywords)
      ? seo.keywords.join(', ')
      : seo.keywords || ''
    const ogImage = seo.image || DEFAULT_OG_IMAGE
    const ogType = seo.ogType || 'website'

    document.title = title
    upsertMeta('name', 'description', description)
    upsertMeta('name', 'keywords', keywords)
    upsertMeta('name', 'robots', seo.robots || 'index,follow')
    upsertMeta('property', 'og:title', title)
    upsertMeta('property', 'og:description', description)
    upsertMeta('property', 'og:url', canonical)
    upsertMeta('property', 'og:type', ogType)
    upsertMeta('property', 'og:site_name', SITE_NAME)
    upsertMeta('property', 'og:image', ogImage)
    upsertMeta('property', 'og:locale', 'en_IN')
    upsertMeta('name', 'twitter:card', seo.twitterCard || 'summary_large_image')
    upsertMeta('name', 'twitter:title', title)
    upsertMeta('name', 'twitter:description', description)
    upsertMeta('name', 'twitter:image', ogImage)
    upsertLink('canonical', canonical)

    if (seo.schema) {
      upsertJsonLd('homvisit-jsonld', seo.schema)
    }
  }

  if (seoSource && typeof seoSource === 'object' && 'value' in seoSource) {
    watch(seoSource, (v) => apply(v), { immediate: true, deep: true })
  } else {
    apply(seoSource)
  }

  onBeforeUnmount(() => {
    const el = document.getElementById('homvisit-jsonld')
    if (el) el.remove()
  })
}

export function organizationSchema() {
  return {
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    email: CONTACT_EMAIL,
    telephone: CONTACT_PHONE_TEL,
    logo: `${SITE_URL}/og-default.svg`,
    sameAs: [],
  }
}

export function localBusinessSchema() {
  return {
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#localbusiness`,
    name: SITE_NAME,
    url: SITE_URL,
    email: CONTACT_EMAIL,
    telephone: CONTACT_PHONE_TEL,
    image: `${SITE_URL}/og-default.svg`,
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Thiruvananthapuram',
      addressRegion: 'Kerala',
      addressCountry: 'IN',
    },
    areaServed: {
      '@type': 'State',
      name: 'Kerala',
    },
    description:
      'Corporate health camps, onsite phlebotomy, and phlebotomist staffing across Kerala.',
    parentOrganization: { '@id': `${SITE_URL}/#organization` },
  }
}

/** MedicalBusiness extends LocalBusiness — keep for healthcare entity signal */
export function medicalBusinessSchema() {
  return {
    '@type': 'MedicalBusiness',
    '@id': `${SITE_URL}/#medicalbusiness`,
    name: SITE_NAME,
    url: SITE_URL,
    email: CONTACT_EMAIL,
    telephone: CONTACT_PHONE_TEL,
    image: `${SITE_URL}/og-default.svg`,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Thiruvananthapuram',
      addressRegion: 'Kerala',
      addressCountry: 'IN',
    },
    areaServed: {
      '@type': 'State',
      name: 'Kerala',
    },
    description:
      'Homvisit provides corporate health camps, onsite phlebotomy teams, and phlebotomist staffing across Kerala.',
  }
}

/**
 * WebSite schema. SearchAction omitted — no /search route exists.
 * Adding a fake SearchAction target would be invalid structured data.
 */
export function websiteSchema() {
  return {
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: SITE_NAME,
    url: SITE_URL,
    publisher: { '@id': `${SITE_URL}/#organization` },
    inLanguage: 'en-IN',
  }
}

export function breadcrumbSchema(items) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.path.startsWith('http') ? item.path : `${SITE_URL}${item.path}`,
    })),
  }
}

export function faqSchema(faqs) {
  if (!faqs || !faqs.length) return null
  return {
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a,
      },
    })),
  }
}

export function serviceSchema({ name, description, area, url }) {
  return {
    '@type': 'Service',
    name,
    description: fitDescription(description, 300),
    url,
    provider: { '@id': `${SITE_URL}/#organization` },
    areaServed: {
      '@type': 'Place',
      name: area,
    },
    serviceType: 'Corporate health camp and onsite phlebotomy',
  }
}

export function jobPostingSchema({ title, description, url, location }) {
  return {
    '@type': 'JobPosting',
    title,
    description,
    datePosted: new Date().toISOString().slice(0, 10),
    validThrough: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365)
      .toISOString()
      .slice(0, 10),
    employmentType: 'CONTRACTOR',
    hiringOrganization: {
      '@type': 'Organization',
      name: SITE_NAME,
      sameAs: SITE_URL,
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: location,
        addressRegion: 'Kerala',
        addressCountry: 'IN',
      },
    },
    applicantLocationRequirements: {
      '@type': 'Country',
      name: 'India',
    },
    directApply: true,
    url,
  }
}

export function graphSchema(nodes) {
  return {
    '@context': 'https://schema.org',
    '@graph': nodes.filter(Boolean),
  }
}
